import { thaiBahtText } from "./receipt-pdf";

type PayrollRow = {
  displayName: string;
  payroll: {
    completeDays: number;
    otHours: number;
    basePay: number;
    otPay: number;
    otherAllowances: number;
    advanceDeduction: number;
    netPay: number;
    dailyRate?: number;
    hourlyRate?: number;
    monthlySalary?: number;
  };
};

const esc = (value: unknown) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const money = (value: number) =>
  `฿${Number(value || 0).toLocaleString("th-TH", { minimumFractionDigits: 2 })}`;

export function openPayrollPdf(input: {
  periodLabel: string;
  startDate: string;
  endDate: string;
  employees: PayrollRow[];
  title?: string;
}) {
  const win = window.open("", "_blank", "noopener,noreferrer");
  if (!win) {
    alert("กรุณาอนุญาต Pop-up บนเบราว์เซอร์เพื่อสร้างเอกสาร PDF ขนาด A4");
    return;
  }

  const printedDateStr = new Date().toLocaleDateString("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  // CASE 1: Individual Payslip (Single Employee) -> A4 Portrait Layout
  if (input.employees.length === 1) {
    const emp = input.employees[0];
    const p = emp.payroll;
    const totalEarnings = (p.basePay || 0) + (p.otPay || 0) + (p.otherAllowances || 0);
    const totalDeductions = p.advanceDeduction || 0;
    const netPay = p.netPay || totalEarnings - totalDeductions;
    const bahtWord = thaiBahtText(netPay);

    const html = `<!DOCTYPE html>
<html lang="th">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${esc(input.title || `ใบรับเงินค่าแรงและ OT — ${emp.displayName}`)}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Sarabun:wght@400;500;600;700;800&display=swap" rel="stylesheet">
  <style>
    @page {
      size: A4 portrait;
      margin: 12mm 15mm 12mm 15mm;
    }
    *, *::before, *::after {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    body {
      font-family: 'Sarabun', 'TH Sarabun New', -apple-system, BlinkMacSystemFont, 'Segoe UI', Tahoma, sans-serif;
      margin: 0;
      padding: 20px;
      color: #0f172a;
      background: #f1f5f9;
      font-size: 13.5px;
      line-height: 1.5;
    }
    .toolbar {
      max-width: 180mm;
      margin: 0 auto 16px auto;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #ffffff;
      padding: 10px 16px;
      border-radius: 12px;
      border: 1px solid #cbd5e1;
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    }
    .toolbar-title {
      font-weight: 700;
      font-size: 14px;
      color: #15803d;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 18px;
      font-size: 13px;
      font-weight: 700;
      font-family: inherit;
      border-radius: 8px;
      cursor: pointer;
      border: 0;
      transition: all 0.15s ease;
    }
    .btn-primary {
      background: #15803d;
      color: #ffffff;
    }
    .btn-primary:hover {
      background: #166534;
    }
    .btn-secondary {
      background: #e2e8f0;
      color: #334155;
      margin-left: 8px;
    }
    .btn-secondary:hover {
      background: #cbd5e1;
    }
    .a4-sheet {
      width: 180mm;
      min-height: 250mm;
      max-width: 100%;
      margin: 0 auto;
      background: #ffffff;
      padding: 22mm 18mm;
      border-radius: 8px;
      border: 1px solid #cbd5e1;
      box-shadow: 0 4px 20px rgba(0,0,0,0.08);
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 2.5px solid #15803d;
      padding-bottom: 12px;
      margin-bottom: 18px;
    }
    .org-title {
      font-size: 22px;
      font-weight: 800;
      color: #15803d;
      margin: 0 0 4px 0;
    }
    .org-sub {
      font-size: 12px;
      color: #64748b;
      font-weight: 500;
    }
    .doc-meta {
      text-align: right;
    }
    .doc-title {
      font-size: 18px;
      font-weight: 800;
      color: #0f172a;
      margin: 0 0 4px 0;
    }
    .doc-sub {
      font-size: 12.5px;
      color: #475569;
      font-weight: 600;
    }
    .info-card {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px 20px;
      background: #f8fafc;
      padding: 12px 18px;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      margin-bottom: 18px;
    }
    .info-row {
      display: flex;
      font-size: 13px;
    }
    .info-label {
      color: #64748b;
      font-weight: 600;
      width: 130px;
      shrink: 0;
    }
    .info-val {
      color: #0f172a;
      font-weight: 700;
    }
    .stats-bar {
      display: flex;
      gap: 12px;
      margin-bottom: 18px;
    }
    .stat-pill {
      flex: 1;
      background: #f0fdf4;
      border: 1px solid #bbf7d0;
      border-radius: 8px;
      padding: 8px 12px;
      text-align: center;
    }
    .stat-pill-label {
      font-size: 11.5px;
      color: #166534;
      font-weight: 600;
    }
    .stat-pill-val {
      font-size: 16px;
      color: #15803d;
      font-weight: 800;
      margin-top: 2px;
    }
    .breakdown-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-bottom: 18px;
    }
    .panel {
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      overflow: hidden;
    }
    .panel-header {
      padding: 8px 12px;
      font-weight: 700;
      font-size: 13px;
      display: flex;
      justify-content: space-between;
    }
    .panel-earnings .panel-header {
      background: #f0fdf4;
      color: #166534;
      border-bottom: 1px solid #bbf7d0;
    }
    .panel-deductions .panel-header {
      background: #fef2f2;
      color: #991b1b;
      border-bottom: 1px solid #fecaca;
    }
    .panel-body {
      padding: 10px 12px;
      background: #ffffff;
    }
    .item-row {
      display: flex;
      justify-content: space-between;
      font-size: 12.5px;
      padding: 5px 0;
      border-bottom: 1px dashed #f1f5f9;
    }
    .item-row:last-child {
      border-bottom: none;
    }
    .item-label {
      color: #475569;
    }
    .item-val {
      font-weight: 700;
      color: #0f172a;
    }
    .panel-footer {
      padding: 8px 12px;
      font-weight: 700;
      font-size: 13px;
      display: flex;
      justify-content: space-between;
      border-top: 1px solid #e2e8f0;
      background: #f8fafc;
    }
    .total-box {
      background: #f0fdf4;
      border: 2px solid #16a34a;
      border-radius: 8px;
      padding: 14px 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
    }
    .total-label {
      font-size: 15px;
      font-weight: 800;
      color: #166534;
    }
    .total-baht {
      font-size: 12.5px;
      color: #15803d;
      font-weight: 600;
    }
    .total-val {
      font-size: 26px;
      font-weight: 800;
      color: #15803d;
    }
    .signatures {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 40px;
      margin-top: 12px;
      padding-top: 14px;
      border-top: 1px dashed #cbd5e1;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .sig-col {
      text-align: center;
    }
    .sig-space {
      height: 48px;
      border-bottom: 1px dotted #94a3b8;
      margin-bottom: 8px;
    }
    .sig-name {
      font-weight: 700;
      font-size: 12.5px;
      color: #0f172a;
    }
    .sig-role {
      font-size: 11.5px;
      color: #64748b;
      margin-top: 2px;
    }
    .sig-date {
      font-size: 11px;
      color: #94a3b8;
      margin-top: 4px;
    }
    .footer-note {
      text-align: center;
      font-size: 10.5px;
      color: #94a3b8;
      margin-top: 22px;
      padding-top: 8px;
      border-top: 1px solid #f1f5f9;
    }
    @media print {
      body {
        background: #ffffff !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      .toolbar {
        display: none !important;
      }
      .a4-sheet {
        width: 100% !important;
        min-height: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <div class="toolbar-title">
      📄 ใบแจ้งยอดค่าแรงรายบุคคล — ขนาด A4 (Portrait)
    </div>
    <div>
      <button class="btn btn-primary" onclick="window.print()">🖨️ พิมพ์เอกสาร / บันทึก PDF</button>
      <button class="btn btn-secondary" onclick="window.close()">✕ ปิด</button>
    </div>
  </div>

  <div class="a4-sheet">
    <div>
      <div class="header">
        <div>
          <h1 class="org-title">DEMO CHECK-IN</h1>
          <div class="org-sub">ระบบบริหารจัดการลงเวลาและบุคลากรอิเล็กทรอนิกส์</div>
        </div>
        <div class="doc-meta">
          <div class="doc-title">ใบรับเงินค่าแรงและค่าล่วงเวลา (OT)</div>
          <div class="doc-sub">รอบคำนวณ: <strong>${esc(input.periodLabel)}</strong></div>
        </div>
      </div>

      <div class="info-card">
        <div class="info-row">
          <span class="info-label">ชื่อ-นามสกุล พนักงาน:</span>
          <span class="info-val">${esc(emp.displayName)}</span>
        </div>
        <div class="info-row">
          <span class="info-label">ช่วงวันที่ทำงาน:</span>
          <span class="info-val">${esc(input.startDate)} ถึง ${esc(input.endDate)}</span>
        </div>
        <div class="info-row">
          <span class="info-label">วันที่ออกเอกสาร:</span>
          <span class="info-val">${printedDateStr}</span>
        </div>
        <div class="info-row">
          <span class="info-label">สถานะการจ่าย:</span>
          <span class="info-val" style="color: #15803d;">พร้อมจ่ายสุทธิ</span>
        </div>
      </div>

      <div class="stats-bar">
        <div class="stat-pill">
          <div class="stat-pill-label">วันทำงานที่มีเวลาครบ</div>
          <div class="stat-pill-val">${p.completeDays} วัน</div>
        </div>
        <div class="stat-pill">
          <div class="stat-pill-label">ชั่วโมงล่วงเวลา (OT)</div>
          <div class="stat-pill-val">${p.otHours} ชม.</div>
        </div>
      </div>

      <div class="breakdown-grid">
        <div class="panel panel-earnings">
          <div class="panel-header">
            <span>รายการได้ (Earnings)</span>
            <span>จำนวนเงิน</span>
          </div>
          <div class="panel-body">
            <div class="item-row">
              <span class="item-label">ค่าแรงฐาน (${p.completeDays} วัน)</span>
              <span class="item-val">${money(p.basePay)}</span>
            </div>
            <div class="item-row">
              <span class="item-label">ค่าล่วงเวลา OT (${p.otHours} ชม.)</span>
              <span class="item-val">${money(p.otPay)}</span>
            </div>
            <div class="item-row">
              <span class="item-label">เงินเพิ่มอื่น / เบี้ยเลี้ยง</span>
              <span class="item-val">${money(p.otherAllowances)}</span>
            </div>
          </div>
          <div class="panel-footer">
            <span>รวมรายได้ทั้งหมด</span>
            <span style="color: #166534;">${money(totalEarnings)}</span>
          </div>
        </div>

        <div class="panel panel-deductions">
          <div class="panel-header">
            <span>รายการหัก (Deductions)</span>
            <span>จำนวนเงิน</span>
          </div>
          <div class="panel-body">
            <div class="item-row">
              <span class="item-label">หักเบิกเงินเดือนล่วงหน้า</span>
              <span class="item-val">${money(p.advanceDeduction)}</span>
            </div>
            <div class="item-row">
              <span class="item-label">หักรายการอื่นๆ</span>
              <span class="item-val">฿0.00</span>
            </div>
            <div class="item-row" style="visibility: hidden;">
              <span class="item-label">placeholder</span>
              <span class="item-val">-</span>
            </div>
          </div>
          <div class="panel-footer">
            <span>รวมรายการหักทั้งหมด</span>
            <span style="color: #991b1b;">${money(totalDeductions)}</span>
          </div>
        </div>
      </div>

      <div class="total-box">
        <div>
          <div class="total-label">ยอดจ่ายสุทธิ (Net Amount Paid)</div>
          <div class="total-baht">(${bahtWord})</div>
        </div>
        <div class="total-val">${money(netPay)}</div>
      </div>
    </div>

    <div>
      <div class="signatures">
        <div class="sig-col">
          <div class="sig-space"></div>
          <div class="sig-name">ลงชื่อ: ${esc(emp.displayName)}</div>
          <div class="sig-role">ผู้รับเงิน / พนักงาน</div>
          <div class="sig-date">วันที่ ......./......./...........</div>
        </div>
        <div class="sig-col">
          <div class="sig-space"></div>
          <div class="sig-name">ลงชื่อ: ....................................................</div>
          <div class="sig-role">ผู้จ่ายเงิน / ฝ่ายการเงินและบัญชี</div>
          <div class="sig-date">วันที่ ......./......./...........</div>
        </div>
      </div>

      <div class="footer-note">
        เอกสารนี้สร้างขึ้นโดยระบบอัตโนมัติ Demo Check-In · เอกสารขนาดกระดาษมาตรฐาน A4
      </div>
    </div>
  </div>
</body>
</html>`;

    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    return;
  }

  // CASE 2: Multi-Employee Summary -> A4 Landscape Layout
  const totals = input.employees.reduce(
    (sum, row) => ({
      base: sum.base + row.payroll.basePay,
      ot: sum.ot + row.payroll.otPay,
      allowance: sum.allowance + row.payroll.otherAllowances,
      deduction: sum.deduction + row.payroll.advanceDeduction,
      net: sum.net + row.payroll.netPay,
      days: sum.days + row.payroll.completeDays,
      otHours: sum.otHours + row.payroll.otHours,
    }),
    { base: 0, ot: 0, allowance: 0, deduction: 0, net: 0, days: 0, otHours: 0 }
  );

  const rows = input.employees
    .map(
      (row, index) => `<tr>
      <td style="text-align: center;">${index + 1}</td>
      <td class="name">${esc(row.displayName)}</td>
      <td style="text-align: center;">${row.payroll.completeDays}</td>
      <td style="text-align: center;">${row.payroll.otHours}</td>
      <td>${money(row.payroll.basePay)}</td>
      <td>${money(row.payroll.otPay)}</td>
      <td>${money(row.payroll.otherAllowances)}</td>
      <td style="color: #b91c1c;">${money(row.payroll.advanceDeduction)}</td>
      <td class="net">${money(row.payroll.netPay)}</td>
    </tr>`
    )
    .join("");

  const landscapeHtml = `<!doctype html>
<html lang="th">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${esc(input.title || "ใบสรุปค่าแรงและ OT ภาพรวม")}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Sarabun:wght@400;500;600;700;800&display=swap" rel="stylesheet">
  <style>
    @page {
      size: A4 landscape;
      margin: 10mm 12mm 10mm 12mm;
    }
    *, *::before, *::after {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    body {
      font-family: 'Sarabun', 'TH Sarabun New', -apple-system, BlinkMacSystemFont, 'Segoe UI', Tahoma, sans-serif;
      margin: 0;
      padding: 16px;
      color: #0f172a;
      background: #f1f5f9;
      font-size: 11.5px;
      line-height: 1.4;
    }
    .toolbar {
      max-width: 273mm;
      margin: 0 auto 12px auto;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #ffffff;
      padding: 10px 16px;
      border-radius: 12px;
      border: 1px solid #cbd5e1;
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    }
    .toolbar-title {
      font-weight: 700;
      font-size: 14px;
      color: #15803d;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 18px;
      font-size: 13px;
      font-weight: 700;
      font-family: inherit;
      border-radius: 8px;
      cursor: pointer;
      border: 0;
      transition: all 0.15s ease;
    }
    .btn-primary {
      background: #15803d;
      color: #ffffff;
    }
    .btn-primary:hover {
      background: #166534;
    }
    .btn-secondary {
      background: #e2e8f0;
      color: #334155;
      margin-left: 8px;
    }
    .btn-secondary:hover {
      background: #cbd5e1;
    }
    .a4-landscape-sheet {
      width: 273mm;
      max-width: 100%;
      margin: 0 auto;
      background: #ffffff;
      padding: 14mm 16mm;
      border-radius: 8px;
      border: 1px solid #cbd5e1;
      box-shadow: 0 4px 20px rgba(0,0,0,0.08);
      position: relative;
    }
    header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 2.5px solid #15803d;
      padding-bottom: 10px;
      margin-bottom: 12px;
    }
    h1 {
      font-size: 19px;
      color: #15803d;
      font-weight: 800;
      margin: 0 0 3px 0;
    }
    .meta {
      text-align: right;
      font-size: 11px;
      color: #475569;
    }
    .meta b {
      color: #0f172a;
      font-size: 12px;
    }
    .summary-bar {
      display: flex;
      gap: 8px;
      background: #f8fafc;
      padding: 8px 12px;
      border-radius: 6px;
      border: 1px solid #e2e8f0;
      margin-bottom: 12px;
      font-size: 11px;
    }
    .summary-item {
      flex: 1;
      text-align: center;
    }
    .summary-item .s-label {
      color: #64748b;
      font-size: 10px;
      font-weight: 600;
    }
    .summary-item .s-val {
      font-weight: 800;
      font-size: 13px;
      color: #0f172a;
      margin-top: 2px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 11px;
      page-break-inside: auto;
    }
    thead {
      display: table-header-group;
    }
    tr {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    th {
      background: #15803d;
      color: white;
      padding: 7px 8px;
      font-size: 11px;
      font-weight: 700;
      border: 1px solid #15803d;
      text-align: right;
    }
    th:first-child {
      text-align: center;
    }
    td {
      border: 1px solid #e2e8f0;
      padding: 6px 8px;
      text-align: right;
    }
    tr:nth-child(even) td {
      background: #f8fafc;
    }
    .name, th:nth-child(2) {
      text-align: left;
      font-weight: 600;
    }
    .net {
      font-weight: 800;
      color: #15803d;
    }
    tfoot {
      display: table-footer-group;
    }
    tfoot td {
      font-weight: 800;
      background: #f0fdf4 !important;
      border-top: 2px solid #15803d;
      color: #14532d;
      font-size: 11.5px;
      padding: 7px 8px;
    }
    .sign {
      display: flex;
      justify-content: space-between;
      margin-top: 28px;
      text-align: center;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .sign div {
      width: 28%;
      border-top: 1px solid #94a3b8;
      padding-top: 6px;
      font-size: 11px;
      color: #334155;
      font-weight: 600;
    }
    .footer-stamp {
      text-align: center;
      font-size: 10px;
      color: #94a3b8;
      margin-top: 20px;
      padding-top: 6px;
      border-top: 1px solid #f1f5f9;
    }
    @media print {
      body {
        background: #ffffff !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      .toolbar {
        display: none !important;
      }
      .a4-landscape-sheet {
        width: 100% !important;
        max-width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
      }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <div class="toolbar-title">
      📄 รายงานสรุปค่าแรงภาพรวม — ขนาด A4 (Landscape)
    </div>
    <div>
      <button class="btn btn-primary" onclick="window.print()">🖨️ พิมพ์เอกสาร / บันทึก PDF</button>
      <button class="btn btn-secondary" onclick="window.close()">✕ ปิด</button>
    </div>
  </div>

  <div class="a4-landscape-sheet">
    <header>
      <div>
        <h1>${esc(input.title || "ใบสรุปค่าแรงและ OT ประจำงวด")}</h1>
        <div style="font-size: 11px; color: #64748b;">Demo Check-In · ระบบบริหารจัดการลงเวลาและบุคลากรอิเล็กทรอนิกส์</div>
      </div>
      <div class="meta">
        <b>งวดคำนวณ: ${esc(input.periodLabel)}</b><br>
        วันที่ ${esc(input.startDate)} ถึง ${esc(input.endDate)}<br>
        ออกเอกสาร: ${printedDateStr}
      </div>
    </header>

    <div class="summary-bar">
      <div class="summary-item">
        <div class="s-label">พนักงานทั้งหมด</div>
        <div class="s-val">${input.employees.length} คน</div>
      </div>
      <div class="summary-item">
        <div class="s-label">วันทำงานสะสม</div>
        <div class="s-val">${totals.days} วัน</div>
      </div>
      <div class="summary-item">
        <div class="s-label">ชั่วโมง OT รวม</div>
        <div class="s-val">${totals.otHours} ชม.</div>
      </div>
      <div class="summary-item">
        <div class="s-label">รวมค่าแรงฐาน</div>
        <div class="s-val">${money(totals.base)}</div>
      </div>
      <div class="summary-item">
        <div class="s-label">รวมค่า OT</div>
        <div class="s-val">${money(totals.ot)}</div>
      </div>
      <div class="summary-item">
        <div class="s-label">รวมเงินเพิ่มอื่น</div>
        <div class="s-val">${money(totals.allowance)}</div>
      </div>
      <div class="summary-item">
        <div class="s-label">รวมหักเบิกล่วงหน้า</div>
        <div class="s-val" style="color: #b91c1c;">${money(totals.deduction)}</div>
      </div>
      <div class="summary-item" style="background: #f0fdf4; border-radius: 4px; padding: 2px;">
        <div class="s-label" style="color: #166534;">ยอดจ่ายสุทธิรวม</div>
        <div class="s-val" style="color: #15803d;">${money(totals.net)}</div>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th style="width: 35px; text-align: center;">#</th>
          <th style="text-align: left;">ชื่อ-นามสกุล พนักงาน</th>
          <th style="width: 75px; text-align: center;">วันทำงานครบ</th>
          <th style="width: 75px; text-align: center;">OT (ชม.)</th>
          <th style="width: 95px;">ค่าแรงฐาน</th>
          <th style="width: 85px;">ค่า OT</th>
          <th style="width: 85px;">รายได้อื่น</th>
          <th style="width: 95px;">หักเบิกล่วงหน้า</th>
          <th style="width: 105px;">ยอดจ่ายสุทธิ</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
      <tfoot>
        <tr>
          <td colspan="2" style="text-align: left;">รวมทั้งสิ้น (${input.employees.length} คน)</td>
          <td style="text-align: center;">${totals.days} วัน</td>
          <td style="text-align: center;">${totals.otHours} ชม.</td>
          <td>${money(totals.base)}</td>
          <td>${money(totals.ot)}</td>
          <td>${money(totals.allowance)}</td>
          <td style="color: #b91c1c;">${money(totals.deduction)}</td>
          <td class="net">${money(totals.net)}</td>
        </tr>
      </tfoot>
    </table>

    <div class="sign">
      <div>
        <div style="height: 35px;"></div>
        <div>ผู้จัดทำรายงาน / เจ้าหน้าที่ HR</div>
        <div style="font-size: 10px; color: #94a3b8; margin-top: 2px;">วันที่ ......./......./...........</div>
      </div>
      <div>
        <div style="height: 35px;"></div>
        <div>ผู้ตรวจสอบ / หัวหน้าแผนกบัญชี</div>
        <div style="font-size: 10px; color: #94a3b8; margin-top: 2px;">วันที่ ......./......./...........</div>
      </div>
      <div>
        <div style="height: 35px;"></div>
        <div>ผู้อนุมัติจ่าย / กรรมการผู้จัดการ</div>
        <div style="font-size: 10px; color: #94a3b8; margin-top: 2px;">วันที่ ......./......./...........</div>
      </div>
    </div>

    <div class="footer-stamp">
      Demo Check-In · ระบบบริหารจัดการลงเวลาและค่าจ้างอิเล็กทรอนิกส์ · เอกสารขนาดมาตรฐาน A4 (แนวนอน)
    </div>
  </div>
</body>
</html>`;

  win.document.open();
  win.document.write(landscapeHtml);
  win.document.close();
  win.focus();
}
